import { Box, Card } from '@shopify/polaris';
import { useMemo, useState } from 'react';
import { useCurrency } from '../../../../customHooks/useCurrency';
import { TableShell } from './TableShell';
import {
  TABLE_BODY_TEXT,
  TABLE_CELL_BORDER,
  TABLE_CELL_PADDING,
  TABLE_DANGER,
  TABLE_HEADER_BG,
  TABLE_HEADER_TEXT_STRONG,
  TABLE_TOTAL_BG,
} from './tableStyles';
import { BREAKDOWN_COLS, aggregateByDimensions } from './aggregateByDimensions';
import { ZERO_EPS, formatMoney, formatPct, formatUnits } from './formatters';

const CELL_BORDER = TABLE_CELL_BORDER;
const DANGER = TABLE_DANGER;

/**
 * Tabla "Por categorías" — agrega las filas del informe en grupos
 * (categoría, o género · categoría si no hay filtro de género). Para
 * cada grupo calcula sumas (uds y €) y agregados ponderados (eficacia,
 * rentabilidad, dto. medio). Total al pie. Ordenada por valor de
 * compra DESC porque suele ser la lectura natural ("qué pesa más").
 */
// Definición de columnas del breakdown. Cada una declara cómo ordena
// (`type: 'text' | 'num'`) y a qué campo del grupo apunta.
/**
 * `dimensions` define el agrupamiento Y qué columnas dimensionales se
 * muestran. P.ej. `['gender', 'category']` agrega por género+categoría y
 * muestra ambas columnas. `['category', 'subcategory']` agrega por
 * categoría+subcategoría y muestra ambas (sin género).
 */
export const BreakdownByCategory = ({ rows, dimensions, bare = false, topControls = null }) => {
  const { currency } = useCurrency();
  const fmt$ = (n) => formatMoney(n, currency);
  const pct = (n) => formatPct(n);
  const [sortBy, setSortBy] = useState('buyValue');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (id) => {
    if (id === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(id);
      const col = BREAKDOWN_COLS.find((c) => c.id === id);
      // Default por tipo: texto ASC, numérico DESC (lo relevante primero).
      setSortDir(col?.type === 'text' ? 'asc' : 'desc');
    }
  };

  // Agrega filas según las dimensiones activas. NO ordena aquí — el
  // orden lo aplica `sortedGroups` para que el click en una cabecera no
  // fuerce a recalcular el agregado.
  const groups = useMemo(() => aggregateByDimensions(rows, dimensions), [rows, dimensions]);

  // Aplica el sort del usuario sobre los groups ya agregados.
  const sortedGroups = useMemo(() => {
    const col = BREAKDOWN_COLS.find((c) => c.id === sortBy);
    const sign = sortDir === 'desc' ? -1 : 1;
    const arr = [...groups];
    arr.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
      if (bv === null || bv === undefined) return -1;
      if (col?.type === 'text') return String(av).localeCompare(String(bv)) * sign;
      return (av - bv) * sign;
    });
    return arr;
  }, [groups, sortBy, sortDir]);

  // Fila TOTAL agregada (suma columnas sumables; eficacia, rentab y
  // dto. se recalculan sobre los sumatorios — nunca media de medias).
  const total = useMemo(() => {
    const acc = {
      families: 0,
      buyUnits: 0,
      buyValue: 0,
      sellUnitsInSeason: 0,
      sellUnitsTotal: 0,
      sellValuePvfTotal: 0,
      sellValuePvpTotal: 0,
      pvpEtiquetaWeightedTotal: 0,
      remainUnits: 0,
      remainValueCI: 0,
    };
    for (const g of groups) {
      acc.families += g.families;
      acc.buyUnits += g.buyUnits;
      acc.buyValue += g.buyValue;
      acc.sellUnitsInSeason += g.sellUnitsInSeason;
      acc.sellUnitsTotal += g.sellUnitsTotal;
      acc.sellValuePvfTotal += g.sellValuePvfTotal;
      acc.sellValuePvpTotal += g.sellValuePvpTotal;
      acc.pvpEtiquetaWeightedTotal += g.pvpEtiquetaWeightedTotal;
      acc.remainUnits += g.remainUnits;
      acc.remainValueCI += g.remainValueCI;
    }
    return {
      ...acc,
      eficacia: acc.buyUnits > 0 ? acc.sellUnitsTotal / acc.buyUnits : null,
      rendimiento: acc.sellValuePvfTotal - acc.buyValue,
      rentabilidad: acc.buyValue > 0 ? (acc.sellValuePvfTotal - acc.buyValue) / acc.buyValue : null,
      dto: acc.pvpEtiquetaWeightedTotal > 0 ? 1 - acc.sellValuePvpTotal / acc.pvpEtiquetaWeightedTotal : null,
    };
  }, [groups]);

  if (sortedGroups.length === 0) return null;

  // Columnas visibles: dimensiones activas + todas las métricas.
  const visibleCols = BREAKDOWN_COLS.filter((c) => !c.dim || dimensions.includes(c.dim));

  const numStyle = {
    ...TABLE_CELL_PADDING,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    borderBottom: CELL_BORDER,
    color: TABLE_BODY_TEXT,
  };
  const txtStyle = {
    ...TABLE_CELL_PADDING,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    borderBottom: CELL_BORDER,
    color: TABLE_BODY_TEXT,
  };

  // Renderiza la celda para un grupo (o total) según el id de columna.
  const renderCell = (col, g, isTotal) => {
    const v = g[col.id];
    const isNum = col.type === 'num';
    const tdBase = isNum ? numStyle : txtStyle;
    const baseWeight = isTotal ? 700 : 400;
    const k = col.id;
    // Columnas dimensionales (género · categoría · subcategoría): un
    // punto más pequeñas (12 px) que las numéricas — el nombre
    // raramente es la información clave de la fila, así no compiten
    // por la atención con los valores.
    if (col.id === 'gender')
      return (
        <td key={k} style={{ ...tdBase, fontWeight: baseWeight, fontSize: 12 }}>
          {isTotal ? '—' : (g.gender ?? '—')}
        </td>
      );
    // Categoría y subcategoría pueden venir con nombres largos
    // ("Pijamas y ropa interior", "Calcetines y media pantalón"…).
    // Las truncamos con elipsis a 150 px y ponemos el `title` en el
    // propio div del texto — así el tooltip nativo del navegador
    // dispara al pasar el ratón sobre la letra, sin necesidad de
    // estar exactamente sobre el padding del td.
    if (col.id === 'category') {
      const val = isTotal ? (dimensions[0] === 'category' ? 'TOTAL' : '—') : (g.category ?? '—');
      return (
        <td key={k} style={{ ...tdBase, fontWeight: baseWeight, fontSize: 12, maxWidth: 150 }}>
          <div title={val} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {val}
          </div>
        </td>
      );
    }
    if (col.id === 'subcategory') {
      const val = isTotal ? (dimensions[0] === 'subcategory' ? 'TOTAL' : '—') : (g.subcategory ?? '—');
      return (
        <td key={k} style={{ ...tdBase, fontWeight: baseWeight, fontSize: 12, maxWidth: 150 }}>
          <div title={val} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {val}
          </div>
        </td>
      );
    }
    if (col.id === 'families' || col.id === 'buyUnits' || col.id === 'sellUnitsTotal')
      return (
        <td key={k} style={{ ...tdBase, fontWeight: baseWeight }}>
          {formatUnits(v)}
        </td>
      );
    if (col.id === 'pvpAvg')
      return (
        <td key={k} style={{ ...tdBase, fontWeight: baseWeight }}>
          {fmt$(v)}
        </td>
      );
    // Money: rojo+bold si negativo (incluso en la fila TOTAL).
    if (col.id === 'buyValue' || col.id === 'sellValuePvfTotal' || col.id === 'rendimiento') {
      const neg = typeof v === 'number' && v < -ZERO_EPS;
      return (
        <td key={k} style={{ ...tdBase, color: neg ? DANGER : 'inherit', fontWeight: neg || isTotal ? 700 : 400 }}>
          {fmt$(v)}
        </td>
      );
    }
    // Porcentajes (eficacia, sell-through, rentabilidad, dto, shareBuy).
    const neg = v !== null && v !== undefined && v < -ZERO_EPS;
    return (
      <td key={k} style={{ ...tdBase, color: neg ? DANGER : 'inherit', fontWeight: neg || isTotal ? 700 : 400 }}>
        {pct(v)}
      </td>
    );
  };

  // Contenido sin Card. Cuando `bare === true` se devuelve tal cual
  // (para componer dentro de un Card padre, p.ej. el tab Análisis que
  // ya provee título + descripción arriba). Cuando `bare === false`
  // se envuelve en <Card><Box padding="5">…</Box></Card>.
  const inner = (
    <>
      {topControls && (
        <>
          <Box minHeight=".5rem" />
          {topControls}
        </>
      )}
      <Box minHeight=".75rem" />
      <TableShell>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                {visibleCols.map((c) => {
                  const isActive = sortBy === c.id;
                  const arrow = isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
                  return (
                    <th
                      key={c.id}
                      onClick={() => handleSort(c.id)}
                      style={{
                        ...TABLE_CELL_PADDING,
                        textAlign: c.type === 'num' ? 'right' : 'left',
                        whiteSpace: 'nowrap',
                        backgroundColor: TABLE_HEADER_BG,
                        color: TABLE_HEADER_TEXT_STRONG,
                        fontSize: 12,
                        fontWeight: isActive ? 700 : 600,
                        cursor: 'pointer',
                        userSelect: 'none',
                        borderBottom: CELL_BORDER,
                      }}
                      title="Ordenar por esta columna"
                    >
                      {c.label}
                      {arrow}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map((g) => (
                <tr key={g.key} className="efftable-row">
                  {visibleCols.map((c) => renderCell(c, g, false))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: TABLE_TOTAL_BG, fontWeight: 600, borderTop: `2px solid ${'#E1E3E5'}` }}>
                {visibleCols.map((c) => renderCell(c, total, true))}
              </tr>
            </tfoot>
          </table>
        </div>
      </TableShell>
    </>
  );

  if (bare) return inner;
  return (
    <Card>
      <Box padding="5">{inner}</Box>
    </Card>
  );
};
